import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  QrCode,
  Wifi,
  WifiOff,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ShoppingCart,
  User,
  Phone,
  Sparkles,
  Bell,
  Volume2,
  LogOut,
  AlertTriangle,
  Inbox,
  Link2,
  Settings,
  Plus,
  Trash2,
  Edit,
  Users,
  Send,
  Save,
  MessageSquare,
  History,
  FileText,
  Package,
  Megaphone,
  Filter,
  CheckSquare,
  Square,
  MapPin
} from 'lucide-react';

const WhatsAppConnectPage = () => {
  const { user, API_URL, getAuthHeader } = useAuth();
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState('loading');
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bakeryConnected, setBakeryConnected] = useState(false);
  
  // WhatsApp Business API state
  const [businessApiStatus, setBusinessApiStatus] = useState(null);
  const [connectionMode, setConnectionMode] = useState('auto'); // 'auto', 'qr', 'business_api'
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('مرحباً! هذه رسالة تجريبية من نظام المخبز.');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [historicalMessages, setHistoricalMessages] = useState([]);
  const [processedOrders, setProcessedOrders] = useState([]);
  
  // Settings state
  const [settings, setSettings] = useState({
    process_groups: true,
    auto_reply_greeting: true,
    greeting_message: '',
    order_confirmation_message: '',
    clarification_message: '',
    customer_not_found_message: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Reply templates
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    trigger: '',
    message: '',
    is_active: true
  });
  
  // Manual reply
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Create order dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Send statement dialog
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [sendingStatement, setSendingStatement] = useState(false);
  
  // Auto-link customer dialog (for unrecognized customers)
  const [linkCustomerDialogOpen, setLinkCustomerDialogOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [processingAutoOrder, setProcessingAutoOrder] = useState(false);
  const [saveLink, setSaveLink] = useState(true);  // Save phone/group link permanently
  
  // Customer links management
  const [customerLinks, setCustomerLinks] = useState([]);
  const [linksDialogOpen, setLinksDialogOpen] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  // Bulk messaging
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkTargetType, setBulkTargetType] = useState('groups'); // 'groups' or 'customers'
  const [selectedGroupsForBulk, setSelectedGroupsForBulk] = useState([]);
  const [selectedCustomersForBulk, setSelectedCustomersForBulk] = useState([]);
  const [bulkFilterArea, setBulkFilterArea] = useState('');
  const [bulkSelectAll, setBulkSelectAll] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkSendProgress, setBulkSendProgress] = useState({ sent: 0, failed: 0, total: 0 });
  const [bulkSendResults, setBulkSendResults] = useState([]);
  const [showBulkResults, setShowBulkResults] = useState(false);
  
  // Bulk message history
  const [bulkMessageHistory, setBulkMessageHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // WhatsApp groups management
  const [whatsappGroups, setWhatsappGroups] = useState([]);
  const [joinedGroups, setJoinedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
  const [newGroupData, setNewGroupData] = useState({ group_id: '', group_name: '', customer_id: '' });
  const [sendToGroupDialogOpen, setSendToGroupDialogOpen] = useState(false);
  const [selectedGroupForMessage, setSelectedGroupForMessage] = useState(null);
  const [groupMessage, setGroupMessage] = useState('');
  const [sendingToGroup, setSendingToGroup] = useState(false);
  
  // Audio notification
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const audioRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const notifiedMessagesRef = useRef(new Set());
  
  // Polling interval
  const pollingRef = useRef(null);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioEnabled && audioRef.current) {
      // Reset audio to start
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.7;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [audioEnabled]);

  // Check for new messages and play sound
  const checkNewMessages = useCallback((newMessages) => {
    if (!newMessages || newMessages.length === 0) return;
    
    // Find messages we haven't notified about
    const unnotifiedMessages = newMessages.filter(msg => {
      const msgKey = msg.id || `${msg.from_phone}-${msg.created_at}`;
      return !notifiedMessagesRef.current.has(msgKey);
    });
    
    if (unnotifiedMessages.length > 0) {
      // Mark as notified
      unnotifiedMessages.forEach(msg => {
        const msgKey = msg.id || `${msg.from_phone}-${msg.created_at}`;
        notifiedMessagesRef.current.add(msgKey);
      });
      
      // Play sound for new messages
      if (lastMessageCountRef.current > 0) { // Don't play on initial load
        playNotificationSound();
        setNewMessageCount(prev => prev + unnotifiedMessages.length);
        
        // Show toast notification
        const latestMsg = unnotifiedMessages[0];
        const senderName = latestMsg.pushName || latestMsg.from_phone || 'عميل';
        toast.info(`📩 رسالة جديدة من ${senderName}`, {
          duration: 4000,
          icon: '🔔'
        });
      }
      
      lastMessageCountRef.current = newMessages.length;
    }
  }, [playNotificationSound]);

  // Reset new message count when viewing messages
  const resetNewMessageCount = () => {
    setNewMessageCount(0);
  };

  // Fetch WhatsApp Business API status
  const fetchBusinessApiStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp-business/status`, { headers: getAuthHeader() });
      setBusinessApiStatus(res.data);
      return res.data;
    } catch (err) {
      console.error('Error fetching business API status:', err);
      return null;
    }
  }, [API_URL, getAuthHeader]);

  // Fetch status (tries both QR service and Business API)
  const fetchStatus = useCallback(async () => {
    try {
      // First check Business API status
      const businessStatus = await fetchBusinessApiStatus();
      
      // Then check QR service
      const res = await axios.get(`${API_URL}/api/whatsapp-service/status`);
      const data = res.data;
      
      // Determine connection mode
      if (businessStatus?.is_configured) {
        setConnectionMode('business_api');
        setConnectionStatus('connected');
        setError(null);
      } else if (data.status === 'connected' || data.qr_available) {
        setConnectionMode('qr');
        setConnectionStatus(data.status || 'disconnected');
        setError(data.error || null);
        
        if (data.qr_available && data.status !== 'connected') {
          const qrRes = await axios.get(`${API_URL}/api/whatsapp-service/qr`);
          if (qrRes.data.qr) {
            setQrCode(qrRes.data.qr);
          }
        } else if (data.status === 'connected') {
          setQrCode(null);
        }
      } else {
        // Neither working - show Business API setup
        setConnectionMode('business_api');
        setConnectionStatus(businessStatus?.is_configured ? 'connected' : 'not_configured');
      }
      
      setLoading(false);
    } catch (err) {
      // QR service not available, check Business API
      const businessStatus = await fetchBusinessApiStatus();
      if (businessStatus?.is_configured) {
        setConnectionMode('business_api');
        setConnectionStatus('connected');
        setError(null);
      } else {
        setConnectionMode('business_api');
        setConnectionStatus('not_configured');
        setError(null);
      }
      setLoading(false);
    }
  }, [API_URL, fetchBusinessApiStatus]);

  // Send test message via Business API
  const sendTestMessage = async () => {
    if (!testPhone.trim()) {
      toast.error('يرجى إدخال رقم الهاتف');
      return;
    }
    
    setSendingTest(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/whatsapp-business/send-test`,
        { phone: testPhone, message: testMessage },
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        toast.success('تم إرسال الرسالة بنجاح!');
      } else {
        toast.error(res.data.error || 'فشل إرسال الرسالة');
      }
    } catch (err) {
      toast.error('فشل إرسال الرسالة');
    } finally {
      setSendingTest(false);
    }
  };

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/settings`, { headers: getAuthHeader() });
      setSettings(res.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [API_URL, getAuthHeader]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/reply-templates`, { headers: getAuthHeader() });
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, [API_URL, getAuthHeader]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/messages-history?limit=50`, {
        headers: getAuthHeader()
      });
      
      const newMessages = res.data.messages || [];
      setMessages(newMessages);
      
      // Check for new messages and play notification
      checkNewMessages(newMessages);
      
      // Extract processed orders
      const orders = newMessages
        .filter(m => m.order_id)
        .map(m => ({
          order_id: m.order_id,
          order_number: m.order_number,
          customer: { name: m.customer_name },
          phone: m.from_phone,
          timestamp: m.created_at
        }));
      setProcessedOrders(orders);
      
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [API_URL, getAuthHeader, checkNewMessages]);

  // Connect bakery
  const connectBakery = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/whatsapp-service/connect-bakery`, {}, {
        headers: getAuthHeader()
      });
      
      if (res.data.success) {
        setBakeryConnected(true);
        toast.success('تم ربط المخبز');
      }
    } catch (err) {
      toast.error('فشل ربط المخبز');
    }
  }, [API_URL, getAuthHeader]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() });
      setProducts(res.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, [API_URL, getAuthHeader]);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() });
      const data = res.data.customers || res.data || [];
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  }, [API_URL, getAuthHeader]);

  // Fetch customer links
  const fetchCustomerLinks = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/customer-links`, { headers: getAuthHeader() });
      setCustomerLinks(res.data || []);
    } catch (err) {
      console.error('Error fetching customer links:', err);
    } finally {
      setLoadingLinks(false);
    }
  }, [API_URL, getAuthHeader]);

  // Delete customer link
  const deleteCustomerLink = async (linkId) => {
    try {
      await axios.delete(`${API_URL}/api/whatsapp/customer-links/${linkId}`, { headers: getAuthHeader() });
      toast.success('تم حذف الربط');
      fetchCustomerLinks();
    } catch (err) {
      toast.error('فشل حذف الربط');
    }
  };

  // Fetch bulk message history
  const fetchBulkHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/bulk-messages`, { headers: getAuthHeader() });
      setBulkMessageHistory(res.data || []);
    } catch (err) {
      console.error('Error fetching bulk history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [API_URL, getAuthHeader]);

  // Fetch WhatsApp groups (saved + joined)
  const fetchWhatsAppGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      // Fetch saved groups
      const savedRes = await axios.get(`${API_URL}/api/whatsapp/groups`, { headers: getAuthHeader() });
      setWhatsappGroups(savedRes.data || []);
      
      // Fetch joined groups from WhatsApp
      const joinedRes = await axios.get(`${API_URL}/api/whatsapp-service/groups`, { headers: getAuthHeader() });
      setJoinedGroups(joinedRes.data?.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, [API_URL, getAuthHeader]);

  // Add WhatsApp group
  const addWhatsAppGroup = async () => {
    if (!newGroupData.group_id || !newGroupData.group_name) {
      toast.error('يرجى إدخال معرف واسم المجموعة');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/whatsapp/groups`, newGroupData, { headers: getAuthHeader() });
      toast.success('تم إضافة المجموعة');
      setAddGroupDialogOpen(false);
      setNewGroupData({ group_id: '', group_name: '', customer_id: '' });
      fetchWhatsAppGroups();
    } catch (err) {
      toast.error('فشل إضافة المجموعة');
    }
  };

  // Delete WhatsApp group
  const deleteWhatsAppGroup = async (groupDocId) => {
    try {
      await axios.delete(`${API_URL}/api/whatsapp/groups/${groupDocId}`, { headers: getAuthHeader() });
      toast.success('تم حذف المجموعة');
      fetchWhatsAppGroups();
    } catch (err) {
      toast.error('فشل حذف المجموعة');
    }
  };

  // Send message to group
  const sendMessageToGroup = async () => {
    if (!selectedGroupForMessage || !groupMessage.trim()) {
      toast.error('يرجى اختيار المجموعة وكتابة الرسالة');
      return;
    }
    
    setSendingToGroup(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/send-to-group`, {
        group_id: selectedGroupForMessage.group_id || selectedGroupForMessage.id,
        message: groupMessage
      }, { headers: getAuthHeader() });
      
      toast.success('تم إرسال الرسالة للمجموعة');
      setSendToGroupDialogOpen(false);
      setGroupMessage('');
      setSelectedGroupForMessage(null);
    } catch (err) {
      toast.error('فشل إرسال الرسالة');
    } finally {
      setSendingToGroup(false);
    }
  };

  // Find customer by phone (check links first, then customer records)
  const findCustomerByPhone = (phone) => {
    if (!phone) return null;
    const cleanPhone = phone.replace(/\D/g, '');
    
    // First check customer links (cached)
    const link = customerLinks.find(l => {
      if (l.phone) {
        const linkPhone = l.phone.replace(/\D/g, '');
        return linkPhone.includes(cleanPhone) || cleanPhone.includes(linkPhone);
      }
      if (l.group_id) {
        return l.group_id === phone || phone.includes(l.group_id);
      }
      return false;
    });
    
    if (link) {
      const linkedCustomer = customers.find(c => c.id === link.customer_id);
      if (linkedCustomer) return linkedCustomer;
    }
    
    // Then check customer records
    return customers.find(c => {
      const customerPhone = (c.phone || '').replace(/\D/g, '');
      const whatsappPhone = (c.whatsapp_phone || '').replace(/\D/g, '');
      return customerPhone.includes(cleanPhone) || cleanPhone.includes(customerPhone) ||
             whatsappPhone.includes(cleanPhone) || cleanPhone.includes(whatsappPhone);
    });
  };

  // Open order dialog
  const openOrderDialog = (msg) => {
    setSelectedMessage(msg);
    const customer = findCustomerByPhone(msg.from_phone);
    setSelectedCustomer(customer);
    setOrderItems([]);
    setOrderDialogOpen(true);
    
    // Fetch products if not loaded
    if (products.length === 0) fetchProducts();
    if (customers.length === 0) fetchCustomers();
  };

  // Add item to order
  const addItemToOrder = (product) => {
    const existingIndex = orderItems.findIndex(item => item.product_id === product.id);
    if (existingIndex >= 0) {
      const newItems = [...orderItems];
      newItems[existingIndex].quantity += 1;
      setOrderItems(newItems);
    } else {
      setOrderItems([...orderItems, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.default_price || 0
      }]);
    }
  };

  // Update item quantity
  const updateItemQuantity = (index, quantity) => {
    if (quantity <= 0) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    } else {
      const newItems = [...orderItems];
      newItems[index].quantity = quantity;
      setOrderItems(newItems);
    }
  };

  // Create order from WhatsApp
  const createOrderFromWhatsApp = async () => {
    if (!selectedCustomer) {
      toast.error('يرجى اختيار العميل');
      return;
    }
    if (orderItems.length === 0) {
      toast.error('يرجى إضافة منتجات للطلب');
      return;
    }

    setCreatingOrder(true);
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // Check if customer already has an order for today
      const existingOrdersRes = await axios.get(
        `${API_URL}/api/orders?customer_id=${selectedCustomer.id}&date=${today}`,
        { headers: getAuthHeader() }
      );
      const existingOrders = existingOrdersRes.data || [];
      
      let total = 0;
      let orderAction = 'created';
      
      if (existingOrders.length > 0) {
        // Update existing order by adding new items
        const existingOrder = existingOrders[0];
        const existingItems = existingOrder.items || [];
        
        // Merge items: add quantities for existing products, add new products
        const mergedItems = [...existingItems];
        for (const newItem of orderItems) {
          const existingIndex = mergedItems.findIndex(i => i.product_id === newItem.product_id);
          if (existingIndex >= 0) {
            mergedItems[existingIndex].quantity += newItem.quantity;
          } else {
            mergedItems.push({
              product_id: newItem.product_id,
              quantity: newItem.quantity
            });
          }
        }
        
        // Update the order
        const updateData = {
          items: mergedItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
          notes: (existingOrder.notes || '') + `\n+ إضافة من WhatsApp - ${selectedMessage?.from_phone || ''}`
        };
        
        const updateRes = await axios.put(
          `${API_URL}/api/orders/${existingOrder.id}`,
          updateData,
          { headers: getAuthHeader() }
        );
        
        total = updateRes.data.total_with_tax || updateRes.data.total_price || 0;
        orderAction = 'updated';
        toast.success(`تم إضافة المنتجات للطلب الموجود - الإجمالي: ${total.toFixed(2)}€`);
      } else {
        // Create new order
        const orderData = {
          customer_id: selectedCustomer.id,
          date: today,
          items: orderItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          notes: `طلب من WhatsApp - ${selectedMessage?.from_phone || ''}`,
          boxes_to_return: 0,
          amount_to_collect: 0
        };

        const res = await axios.post(`${API_URL}/api/orders`, orderData, { headers: getAuthHeader() });
        const createdOrder = res.data;
        total = createdOrder.total_with_tax || createdOrder.total_price || 0;
        toast.success(`تم إنشاء الطلب بنجاح - الإجمالي: ${total.toFixed(2)}€`);
      }
      
      setOrderDialogOpen(false);
      setOrderItems([]);
      setSelectedCustomer(null);
      
      // Send confirmation via WhatsApp
      if (selectedMessage?.from_phone) {
        const itemsList = orderItems.map(i => `• ${i.product_name} × ${i.quantity}`).join('\n');
        const confirmMsg = orderAction === 'updated' 
          ? `✅ تم إضافة المنتجات لطلبك اليوم!\n\n📦 المنتجات المضافة:\n${itemsList}\n\n💰 الإجمالي الجديد: ${total.toFixed(2)}€\n\nشكراً لتعاملكم معنا! 🥐`
          : `✅ تم استلام طلبك!\n\n📦 المنتجات:\n${itemsList}\n\n💰 الإجمالي: ${total.toFixed(2)}€\n\nشكراً لتعاملكم معنا! 🥐`;
        
        try {
          await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
            phone: selectedMessage.from_phone,
            message: confirmMsg
          }, { headers: getAuthHeader() });
          toast.success('تم إرسال التأكيد للعميل');
        } catch (replyErr) {
          console.error('Failed to send WhatsApp confirmation:', replyErr);
          toast.warning('تم إنشاء الطلب لكن فشل إرسال التأكيد');
        }
      }
      
      fetchMessages();
    } catch (err) {
      console.error('Error creating order:', err);
      const errorMsg = err.response?.data?.detail || 'فشل إنشاء الطلب';
      toast.error(errorMsg);
    } finally {
      setCreatingOrder(false);
    }
  };

  // Open statement dialog
  const openStatementDialog = async (msg) => {
    setSelectedMessage(msg);
    const customer = findCustomerByPhone(msg.from_phone);
    
    if (customer) {
      // Fetch fresh customer data
      try {
        const res = await axios.get(`${API_URL}/api/customers/${customer.id}`, { headers: getAuthHeader() });
        setSelectedCustomer(res.data);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setSelectedCustomer(customer);
      }
    } else {
      setSelectedCustomer(null);
    }
    
    setStatementDialogOpen(true);
    
    if (customers.length === 0) fetchCustomers();
  };

  // Send customer statement
  const sendCustomerStatement = async () => {
    if (!selectedCustomer) {
      toast.error('العميل غير موجود');
      return;
    }

    setSendingStatement(true);
    try {
      // Fetch fresh customer data with accurate balance
      const customerRes = await axios.get(`${API_URL}/api/customers/${selectedCustomer.id}`, { headers: getAuthHeader() });
      const freshCustomer = customerRes.data;
      
      const balance = freshCustomer.balance || 0;
      const totalDebt = freshCustomer.total_debt || 0;
      const totalPaid = freshCustomer.total_paid || 0;
      
      const statementMsg = `📊 كشف حساب العميل\n━━━━━━━━━━━━━━\n👤 ${freshCustomer.name}\n${freshCustomer.shop_name ? `🏪 ${freshCustomer.shop_name}\n` : ''}\n💰 إجمالي المبيعات: ${totalDebt.toFixed(2)}€\n✅ إجمالي المدفوع: ${totalPaid.toFixed(2)}€\n\n${balance > 0 ? `🔴 المتبقي عليك: ${balance.toFixed(2)}€` : balance < 0 ? `🟢 لك رصيد: ${Math.abs(balance).toFixed(2)}€` : '🟢 الحساب مُسدد'}\n━━━━━━━━━━━━━━\nشكراً لتعاملكم معنا! 🥐`;
      
      await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
        phone: selectedMessage?.from_phone,
        message: statementMsg
      }, { headers: getAuthHeader() });
      
      toast.success('تم إرسال كشف الحساب');
      setStatementDialogOpen(false);
    } catch (err) {
      console.error('Error sending statement:', err);
      toast.error('فشل إرسال كشف الحساب');
    } finally {
      setSendingStatement(false);
    }
  };

  // Open link customer dialog for unrecognized messages
  const openLinkCustomerDialog = (msg) => {
    setPendingMessage(msg);
    setSelectedCustomer(null);
    setLinkCustomerDialogOpen(true);
    
    if (customers.length === 0) fetchCustomers();
  };

  // Process message with AI after linking customer
  const processMessageWithAI = async () => {
    if (!selectedCustomer || !pendingMessage) {
      toast.error('يرجى اختيار العميل');
      return;
    }

    setProcessingAutoOrder(true);
    try {
      // Call AI to analyze the message
      const res = await axios.post(`${API_URL}/api/whatsapp/analyze-and-create-order`, {
        message_id: pendingMessage.id,
        message_text: pendingMessage.body,
        customer_id: selectedCustomer.id,
        phone: pendingMessage.from_phone
      }, { headers: getAuthHeader() });

      if (res.data.success) {
        toast.success(`✅ تم إنشاء الطلب تلقائياً - ${res.data.order_number || ''}`);
        
        // Send confirmation to customer
        if (res.data.confirmation_message && pendingMessage.from_phone) {
          try {
            await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
              phone: pendingMessage.from_phone,
              message: res.data.confirmation_message
            }, { headers: getAuthHeader() });
          } catch (e) {
            console.error('Failed to send confirmation:', e);
          }
        }
        
        setLinkCustomerDialogOpen(false);
        setPendingMessage(null);
        setSelectedCustomer(null);
        fetchMessages();
      } else if (res.data.needs_clarification) {
        toast.warning(res.data.clarification_message || 'الرسالة تحتاج توضيح');
        // Keep dialog open for manual order creation
        openOrderDialog(pendingMessage);
        setLinkCustomerDialogOpen(false);
      } else {
        toast.error(res.data.error || 'فشل تحليل الرسالة');
      }
    } catch (err) {
      console.error('Error processing message:', err);
      toast.error(err.response?.data?.detail || 'فشل معالجة الرسالة');
    } finally {
      setProcessingAutoOrder(false);
    }
  };

  // Create order manually after linking (fallback)
  const createOrderAfterLinking = () => {
    if (!selectedCustomer) {
      toast.error('يرجى اختيار العميل');
      return;
    }
    
    // Open manual order dialog with pre-selected customer
    setSelectedMessage(pendingMessage);
    setOrderItems([]);
    setLinkCustomerDialogOpen(false);
    setOrderDialogOpen(true);
    
    if (products.length === 0) fetchProducts();
  };

  // Bulk messaging functions
  
  // Get unique areas from customers
  const customerAreas = [...new Set(customers.filter(c => c.area).map(c => c.area))];

  // Filter customers by area
  const filteredCustomersForBulk = bulkFilterArea 
    ? customers.filter(c => c.area === bulkFilterArea)
    : customers;

  // Customers with WhatsApp numbers
  const customersWithWhatsApp = filteredCustomersForBulk.filter(c => c.phone || c.whatsapp_phone);

  // All available groups (saved + joined)
  const allGroups = [
    ...whatsappGroups.map(g => ({ ...g, source: 'saved' })),
    ...joinedGroups.filter(jg => !whatsappGroups.some(wg => wg.group_id === jg.id))
      .map(g => ({ group_id: g.id, group_name: g.name, source: 'joined', participants: g.participants }))
  ];

  // Handle select all groups
  const handleBulkSelectAllGroups = (checked) => {
    setBulkSelectAll(checked);
    if (checked) {
      setSelectedGroupsForBulk(allGroups.map(g => g.group_id || g.id));
    } else {
      setSelectedGroupsForBulk([]);
    }
  };

  // Toggle group selection
  const toggleGroupSelection = (groupId) => {
    setSelectedGroupsForBulk(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Handle select all customers
  const handleBulkSelectAll = (checked) => {
    setBulkSelectAll(checked);
    if (checked) {
      setSelectedCustomersForBulk(customersWithWhatsApp.map(c => c.id));
    } else {
      setSelectedCustomersForBulk([]);
    }
  };

  // Toggle customer selection
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomersForBulk(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Send bulk messages to groups or customers
  const sendBulkMessages = async () => {
    if (!bulkMessage.trim()) {
      toast.error('يرجى كتابة الرسالة');
      return;
    }
    
    const isGroupMode = bulkTargetType === 'groups';
    const targets = isGroupMode ? selectedGroupsForBulk : selectedCustomersForBulk;
    
    if (targets.length === 0) {
      toast.error(isGroupMode ? 'يرجى اختيار مجموعة واحدة على الأقل' : 'يرجى اختيار عميل واحد على الأقل');
      return;
    }

    setSendingBulk(true);
    setBulkSendProgress({ sent: 0, failed: 0, total: targets.length });
    setBulkSendResults([]);

    const results = [];
    let sent = 0;
    let failed = 0;

    if (isGroupMode) {
      // Send to groups
      for (const groupId of targets) {
        const group = allGroups.find(g => (g.group_id || g.id) === groupId);
        const groupName = group?.group_name || group?.name || groupId;

        try {
          await axios.post(`${API_URL}/api/whatsapp/send-to-group`, {
            group_id: groupId,
            message: bulkMessage
          }, { headers: getAuthHeader() });

          results.push({ name: groupName, type: 'group', status: 'sent' });
          sent++;
        } catch (err) {
          results.push({ 
            name: groupName, 
            type: 'group',
            status: 'failed', 
            error: err.response?.data?.detail || 'فشل الإرسال' 
          });
          failed++;
        }

        setBulkSendProgress({ sent, failed, total: targets.length });
        
        // Small delay between messages
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } else {
      // Send to customers
      for (const customerId of targets) {
        const customer = customers.find(c => c.id === customerId);
        if (!customer) continue;

        const phone = customer.whatsapp_phone || customer.phone;
        if (!phone) {
          results.push({ name: customer.name, type: 'customer', status: 'failed', error: 'لا يوجد رقم' });
          failed++;
          setBulkSendProgress({ sent, failed, total: targets.length });
          continue;
        }

        try {
          await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
            phone: phone,
            message: bulkMessage
          }, { headers: getAuthHeader() });

          results.push({ name: customer.name, phone, type: 'customer', status: 'sent' });
          sent++;
        } catch (err) {
          results.push({ 
            name: customer.name, 
            phone,
            type: 'customer',
            status: 'failed', 
            error: err.response?.data?.detail || 'فشل الإرسال' 
          });
          failed++;
        }

        setBulkSendProgress({ sent, failed, total: targets.length });
        
        // Small delay between messages
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Log the bulk send
    try {
      await axios.post(`${API_URL}/api/whatsapp/bulk-messages`, {
        message: bulkMessage,
        recipients: targets,
        recipient_type: isGroupMode ? 'groups' : 'customers'
      }, { headers: getAuthHeader() });
    } catch (err) {
      console.error('Failed to log bulk message:', err);
    }

    setBulkSendResults(results);
    setShowBulkResults(true);
    setSendingBulk(false);
    fetchBulkHistory();
    
    toast.success(`تم إرسال ${sent} رسالة بنجاح${failed > 0 ? ` (${failed} فشل)` : ''}`);
  };

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchSettings();
    fetchTemplates();
    fetchMessages();
    fetchCustomers();
    fetchCustomerLinks();
    fetchWhatsAppGroups();
    fetchBulkHistory();
    
    if (user?.id) {
      connectBakery();
    }
    
    // Polling
    pollingRef.current = setInterval(() => {
      fetchStatus();
      if (connectionStatus === 'connected') {
        fetchMessages();
      }
    }, 5000);
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [user?.id]); // eslint-disable-line

  // Save settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/whatsapp/settings`, settings, { headers: getAuthHeader() });
      toast.success('تم حفظ الإعدادات');
    } catch (err) {
      toast.error('فشل حفظ الإعدادات');
    } finally {
      setSavingSettings(false);
    }
  };

  // Template handlers
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        trigger: template.trigger,
        message: template.message,
        is_active: template.is_active
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: '', trigger: '', message: '', is_active: true });
    }
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/whatsapp/reply-templates/${editingTemplate.id}`, templateForm, { headers: getAuthHeader() });
        toast.success('تم تحديث القالب');
      } else {
        await axios.post(`${API_URL}/api/whatsapp/reply-templates`, templateForm, { headers: getAuthHeader() });
        toast.success('تم إضافة القالب');
      }
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error('فشل حفظ القالب');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا القالب؟')) return;
    
    try {
      await axios.delete(`${API_URL}/api/whatsapp/reply-templates/${templateId}`, { headers: getAuthHeader() });
      toast.success('تم حذف القالب');
      fetchTemplates();
    } catch (err) {
      toast.error('فشل حذف القالب');
    }
  };

  // Manual reply
  const openReplyDialog = (msg) => {
    setReplyTo(msg);
    setReplyMessage('');
    setReplyDialogOpen(true);
  };

  const sendReply = async () => {
    if (!replyTo || !replyMessage.trim()) return;
    
    setSendingReply(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
        phone: replyTo.from_phone || replyTo.from,
        message: replyMessage
      }, { headers: getAuthHeader() });
      
      toast.success('تم إرسال الرد');
      setReplyDialogOpen(false);
    } catch (err) {
      toast.error('فشل إرسال الرد');
    } finally {
      setSendingReply(false);
    }
  };

  // Restart/Logout
  const restartWhatsApp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp-service/restart`, {}, { headers: getAuthHeader() });
      toast.info('جاري إعادة تشغيل WhatsApp...');
      setTimeout(() => { fetchStatus(); setLoading(false); }, 3000);
    } catch (err) {
      toast.error('فشل إعادة التشغيل');
      setLoading(false);
    }
  };

  const logoutWhatsApp = async () => {
    if (!window.confirm('هل أنت متأكد من تسجيل الخروج؟')) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp-service/logout`, {}, { headers: getAuthHeader() });
      setQrCode(null);
      setConnectionStatus('disconnected');
      toast.success('تم تسجيل الخروج');
      setTimeout(() => { fetchStatus(); setLoading(false); }, 2000);
    } catch (err) {
      toast.error('فشل تسجيل الخروج');
      setLoading(false);
    }
  };

  // Status badge
  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'connected': return <Badge className="bg-green-500 text-white gap-1"><Wifi className="w-3 h-3" /> متصل</Badge>;
      case 'waiting_for_qr_scan': return <Badge className="bg-yellow-500 text-white gap-1"><QrCode className="w-3 h-3" /> انتظار المسح</Badge>;
      case 'error': return <Badge className="bg-red-500 text-white gap-1"><AlertTriangle className="w-3 h-3" /> خطأ</Badge>;
      default: return <Badge className="bg-gray-500 text-white gap-1"><WifiOff className="w-3 h-3" /> غير متصل</Badge>;
    }
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'order_created': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'customer_not_found': return <User className="w-4 h-4 text-orange-500" />;
      case 'needs_clarification': return <MessageCircle className="w-4 h-4 text-yellow-500" />;
      case 'auto_replied': return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'history': return <History className="w-4 h-4 text-gray-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <audio ref={audioRef} src="/notification.wav" preload="auto" />
      
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">WhatsApp التلقائي</h1>
            <p className="text-stone-500">استقبال الطلبات تلقائياً من WhatsApp</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getStatusBadge()}
          {bakeryConnected && (
            <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
              <Link2 className="w-3 h-3" /> مربوط
            </Badge>
          )}
          
          {/* New messages indicator */}
          {newMessageCount > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse gap-1">
              <Bell className="w-3 h-3" />
              {newMessageCount} جديدة
            </Badge>
          )}
          
          {/* Audio toggle */}
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => setAudioEnabled(!audioEnabled)}
            title={audioEnabled ? 'إيقاف الصوت' : 'تفعيل الصوت'}
            className={audioEnabled ? 'border-green-300 text-green-600' : 'text-gray-400'}
          >
            {audioEnabled ? <Volume2 className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="connection" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 max-w-3xl">
          <TabsTrigger value="connection" className="gap-2">
            <QrCode className="w-4 h-4" /> الاتصال
          </TabsTrigger>
          <TabsTrigger value="messages" className="gap-2 relative" onClick={resetNewMessageCount}>
            <Inbox className="w-4 h-4" /> الرسائل
            {newMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {newMessageCount > 9 ? '9+' : newMessageCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="bulk" className="gap-2">
            <Megaphone className="w-4 h-4" /> جماعية
          </TabsTrigger>
          <TabsTrigger value="links" className="gap-2">
            <Link2 className="w-4 h-4" /> الروابط
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <MessageSquare className="w-4 h-4" /> الردود
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" /> الإعدادات
          </TabsTrigger>
        </TabsList>

        {/* Connection Tab */}
        <TabsContent value="connection">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Connection Status Card */}
            <Card className="border-2 border-green-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  حالة الاتصال
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading && connectionStatus === 'loading' ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-12 h-12 text-green-500 animate-spin mb-3" />
                    <p className="text-stone-500">جاري الاتصال...</p>
                  </div>
                ) : connectionMode === 'business_api' && businessApiStatus?.is_configured ? (
                  /* Business API Connected */
                  <div className="text-center py-6">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-green-600 mb-2">متصل عبر Business API</h3>
                    <p className="text-stone-500 mb-4">WhatsApp جاهز للإرسال والاستقبال</p>
                    
                    {/* Test Message Section */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg text-right space-y-3">
                      <Label className="text-sm font-medium">إرسال رسالة تجريبية</Label>
                      <Input
                        placeholder="رقم الهاتف (مثال: 4915123456789)"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        className="text-left"
                        dir="ltr"
                      />
                      <Textarea
                        placeholder="الرسالة"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        rows={2}
                      />
                      <Button 
                        onClick={sendTestMessage} 
                        className="w-full gap-2 bg-green-600 hover:bg-green-700"
                        disabled={sendingTest}
                      >
                        {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        إرسال رسالة تجريبية
                      </Button>
                    </div>
                  </div>
                ) : connectionStatus === 'connected' && connectionMode === 'qr' ? (
                  /* QR Connected */
                  <div className="text-center py-6">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-green-600 mb-2">متصل بنجاح!</h3>
                    <p className="text-stone-500 mb-4">WhatsApp جاهز لاستقبال الرسائل</p>
                    
                    <div className="flex flex-col gap-2">
                      <Button variant="outline" onClick={connectBakery} className="w-full gap-2">
                        <RefreshCw className="w-4 h-4" /> تحديث الربط
                      </Button>
                      <Button variant="outline" onClick={logoutWhatsApp} className="w-full gap-2 text-red-600" disabled={loading}>
                        <LogOut className="w-4 h-4" /> تسجيل الخروج
                      </Button>
                    </div>
                  </div>
                ) : qrCode ? (
                  /* QR Code Display */
                  <div className="text-center">
                    <p className="text-stone-600 mb-3">امسح رمز QR من تطبيق WhatsApp</p>
                    <div className="bg-white p-3 rounded-lg shadow-inner inline-block">
                      <img src={qrCode} alt="WhatsApp QR Code" className="w-56 h-56" />
                    </div>
                    <p className="text-sm text-stone-400 mt-3">WhatsApp → الإعدادات → الأجهزة المرتبطة</p>
                    <Button variant="outline" onClick={fetchStatus} className="mt-4 gap-2">
                      <RefreshCw className="w-4 h-4" /> تحديث
                    </Button>
                  </div>
                ) : (
                  /* Not Connected - Show Business API Setup */
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Phone className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-medium text-stone-700 mb-2">إعداد WhatsApp Business API</h3>
                    <p className="text-stone-500 text-sm mb-4">للاستخدام في الإنتاج، استخدم WhatsApp Business API الرسمي</p>
                    
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-right space-y-3">
                      <h4 className="font-medium text-blue-800 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        خطوات الإعداد:
                      </h4>
                      <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                        <li>أنشئ حساب على <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="underline">Meta Business</a></li>
                        <li>فعّل WhatsApp Business API</li>
                        <li>احصل على Phone Number ID و Access Token</li>
                        <li>أضفهما في إعدادات البيئة (Backend)</li>
                      </ol>
                      
                      <div className="mt-3 p-3 bg-white rounded border text-xs text-stone-600">
                        <p className="font-medium mb-1">المتغيرات المطلوبة:</p>
                        <code className="block bg-gray-100 p-2 rounded text-left" dir="ltr">
                          WHATSAPP_PHONE_NUMBER_ID=your_id<br/>
                          WHATSAPP_ACCESS_TOKEN=your_token
                        </code>
                      </div>
                    </div>
                    
                    <Button onClick={fetchStatus} variant="outline" className="mt-4 gap-2">
                      <RefreshCw className="w-4 h-4" /> تحقق من الاتصال
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  كيف يعمل النظام؟
                </h3>
                <div className="space-y-3 text-sm text-green-700">
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <p>امسح رمز QR من تطبيق WhatsApp</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <p>يقرأ النظام الرسائل من الأفراد والمجموعات</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <p>يحلل الذكاء الاصطناعي الرسائل ويكتشف الطلبات</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <p>يُنشئ الطلب تلقائياً ويرسل رد مخصص للزبون</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-100 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>ملاحظة:</strong> يجب تسجيل رقم WhatsApp للزبون في بيانات العميل
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-blue-600" />
                  الرسائل الواردة
                  {messages.length > 0 && <Badge variant="secondary">{messages.length}</Badge>}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchMessages}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لم تصل رسائل بعد</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {messages.map((msg, idx) => {
                    const customer = findCustomerByPhone(msg.from_phone);
                    return (
                    <div 
                      key={msg.id || idx}
                      className={`p-4 rounded-lg border ${
                        msg.status === 'order_created' ? 'bg-green-50 border-green-200' :
                        msg.status === 'error' ? 'bg-red-50 border-red-200' :
                        msg.is_group ? 'bg-purple-50 border-purple-200' :
                        'bg-white border-stone-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Customer/Phone Info */}
                          <div className="flex items-center gap-2 mb-2">
                            {msg.is_group ? <Users className="w-4 h-4 text-purple-500" /> : <User className="w-4 h-4 text-blue-500" />}
                            {customer ? (
                              <span className="font-bold text-stone-800">{customer.name}</span>
                            ) : msg.customer_name ? (
                              <span className="font-bold text-stone-800">{msg.customer_name}</span>
                            ) : (
                              <span className="text-orange-600 font-medium">عميل غير مسجل</span>
                            )}
                            <span className="text-stone-400 text-sm" dir="ltr">+{msg.from_phone}</span>
                            {msg.is_group && <Badge variant="outline" className="text-purple-600 text-xs">مجموعة</Badge>}
                            {getMessageStatusIcon(msg.status)}
                          </div>
                          
                          {/* Shop name if available */}
                          {customer?.shop_name && (
                            <p className="text-sm text-stone-500 mb-1">🏪 {customer.shop_name}</p>
                          )}
                          
                          {/* Message body */}
                          <p className="text-stone-700 bg-white/50 p-2 rounded border border-stone-100">{msg.body}</p>
                          
                          {/* Order created badge */}
                          {msg.status === 'order_created' && msg.order_number && (
                            <div className="mt-2 p-2 bg-green-100 rounded text-sm inline-flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-green-700">تم إنشاء طلب #{msg.order_number}</span>
                            </div>
                          )}
                          
                          {/* Customer not found - link customer banner */}
                          {(msg.status === 'customer_not_found' || (!customer && msg.status !== 'order_created')) && (
                            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-orange-700 text-sm">
                                  <AlertTriangle className="w-4 h-4" />
                                  <span>عميل غير مسجل - اختر العميل لإنشاء الطلب</span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => openLinkCustomerDialog(msg)}
                                  className="gap-1 bg-orange-600 hover:bg-orange-700 text-white"
                                >
                                  <Users className="w-3 h-3" />
                                  ربط العميل
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-stone-400">
                            {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('ar-EG', {
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : ''}
                          </span>
                          
                          <div className="flex gap-1">
                            {/* Auto-process with AI (for known customers) */}
                            {customer && msg.status !== 'order_created' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={async () => {
                                  // Quick auto-process
                                  setPendingMessage(msg);
                                  setSelectedCustomer(customer);
                                  try {
                                    const res = await axios.post(`${API_URL}/api/whatsapp/analyze-and-create-order`, {
                                      message_id: msg.id,
                                      message_text: msg.body,
                                      customer_id: customer.id,
                                      phone: msg.from_phone
                                    }, { headers: getAuthHeader() });
                                    
                                    if (res.data.success) {
                                      toast.success(`✅ تم إنشاء الطلب - ${res.data.order_number}`);
                                      if (res.data.confirmation_message) {
                                        await axios.post(`${API_URL}/api/whatsapp/send-reply`, {
                                          phone: msg.from_phone,
                                          message: res.data.confirmation_message
                                        }, { headers: getAuthHeader() });
                                      }
                                      fetchMessages();
                                    } else if (res.data.needs_clarification) {
                                      toast.warning('الرسالة تحتاج توضيح - استخدم الإنشاء اليدوي');
                                      openOrderDialog(msg);
                                    }
                                  } catch (err) {
                                    toast.error('فشل تحليل الرسالة');
                                    openOrderDialog(msg);
                                  }
                                }}
                                className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                                title="تحليل وإنشاء تلقائي"
                              >
                                <Sparkles className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {/* Create Order Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openOrderDialog(msg)}
                              className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
                              title="إنشاء طلب"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                            
                            {/* Send Statement Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openStatementDialog(msg)}
                              className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                              title="إرسال كشف حساب"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            
                            {/* Reply Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openReplyDialog(msg)}
                              className="gap-1 text-stone-600"
                              title="إرسال رد"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Messages Tab */}
        <TabsContent value="bulk">
          <Card className="border-2 border-purple-100">
            <CardHeader className="bg-gradient-to-l from-purple-50 to-fuchsia-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-purple-600" />
                    إرسال رسائل جماعية
                  </CardTitle>
                  <CardDescription>
                    أرسل رسالة لجميع العملاء أو مجموعة محددة منهم
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Message input */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-purple-600" />
                  نص الرسالة
                </Label>
                <textarea
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  placeholder="اكتب رسالتك هنا... 

مثال: 
🥐 مرحباً بكم في مخبزنا!
لدينا عروض خاصة هذا الأسبوع
تواصلوا معنا للطلب 📱"
                  className="w-full h-32 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                  dir="rtl"
                />
                <div className="flex justify-between text-sm text-stone-400">
                  <span>{bulkMessage.length} حرف</span>
                  <span>الحد الأقصى: 1000 حرف</span>
                </div>
              </div>

              {/* Filter by area */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-stone-500" />
                  تصفية حسب المنطقة (اختياري)
                </Label>
                <select
                  value={bulkFilterArea}
                  onChange={(e) => {
                    setBulkFilterArea(e.target.value);
                    setSelectedCustomersForBulk([]);
                    setBulkSelectAll(false);
                  }}
                  className="w-full p-2 border rounded-lg bg-white"
                >
                  <option value="">جميع المناطق</option>
                  {customerAreas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              {/* Customer selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-stone-500" />
                    اختر العملاء ({customersWithWhatsApp.length} عميل لديهم رقم)
                  </Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkSelectAll}
                      onChange={(e) => handleBulkSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-purple-300 text-purple-600"
                    />
                    <span className="text-sm font-medium">تحديد الكل</span>
                  </label>
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1 bg-stone-50">
                  {customersWithWhatsApp.length === 0 ? (
                    <p className="text-center text-stone-400 py-4">لا يوجد عملاء لديهم أرقام WhatsApp</p>
                  ) : (
                    customersWithWhatsApp.map(customer => (
                      <label 
                        key={customer.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedCustomersForBulk.includes(customer.id) 
                            ? 'bg-purple-100 border border-purple-300' 
                            : 'bg-white border border-transparent hover:bg-purple-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCustomersForBulk.includes(customer.id)}
                          onChange={() => toggleCustomerSelection(customer.id)}
                          className="w-4 h-4 rounded border-purple-300 text-purple-600"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{customer.name}</span>
                          {customer.shop_name && (
                            <span className="text-sm text-stone-500 mr-2">({customer.shop_name})</span>
                          )}
                        </div>
                        {customer.area && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <MapPin className="w-3 h-3" />
                            {customer.area}
                          </Badge>
                        )}
                        <span className="text-xs text-stone-400 font-numbers">
                          {customer.whatsapp_phone || customer.phone}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-purple-600 font-medium">
                    تم اختيار: {selectedCustomersForBulk.length} عميل
                  </span>
                </div>
              </div>

              {/* Send progress */}
              {sendingBulk && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-purple-700 font-medium flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      جاري الإرسال...
                    </span>
                    <span className="text-sm text-purple-600">
                      {bulkSendProgress.sent + bulkSendProgress.failed} / {bulkSendProgress.total}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 transition-all duration-300"
                      style={{ 
                        width: `${((bulkSendProgress.sent + bulkSendProgress.failed) / bulkSendProgress.total) * 100}%` 
                      }}
                    />
                  </div>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-green-600">✓ تم إرسال: {bulkSendProgress.sent}</span>
                    {bulkSendProgress.failed > 0 && (
                      <span className="text-red-600">✗ فشل: {bulkSendProgress.failed}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Results */}
              {showBulkResults && bulkSendResults.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>نتائج الإرسال</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowBulkResults(false)}
                    >
                      إخفاء
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-stone-50">
                    {bulkSendResults.map((result, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          result.status === 'sent' ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <span>{result.customer}</span>
                        {result.status === 'sent' ? (
                          <Badge className="bg-green-500">✓ تم الإرسال</Badge>
                        ) : (
                          <Badge variant="destructive">{result.error || 'فشل'}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send button */}
              <Button
                onClick={sendBulkMessages}
                disabled={sendingBulk || !bulkMessage.trim() || selectedCustomersForBulk.length === 0}
                className="w-full gap-2 bg-purple-600 hover:bg-purple-700 text-lg py-6"
              >
                {sendingBulk ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    جاري الإرسال...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    إرسال لـ {selectedCustomersForBulk.length} عميل
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Links Tab */}
        <TabsContent value="links">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-amber-600" />
                    روابط الأرقام والمجموعات
                  </CardTitle>
                  <CardDescription>
                    الأرقام والمجموعات المربوطة بالعملاء - يتم التعرف عليهم تلقائياً
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchCustomerLinks}
                  disabled={loadingLinks}
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingLinks ? 'animate-spin' : ''}`} />
                  تحديث
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLinks ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
                </div>
              ) : customerLinks.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Link2 className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <h3 className="text-lg font-medium mb-2">لا توجد روابط محفوظة</h3>
                  <p className="text-sm text-stone-400 max-w-md mx-auto">
                    عند ربط عميل برقم هاتف أو مجموعة WhatsApp، سيتم حفظ الربط هنا وسيتعرف النظام على العميل تلقائياً في المستقبل
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-stone-500 mb-4">
                    <span>إجمالي الروابط: {customerLinks.length}</span>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="gap-1">
                        <Phone className="w-3 h-3" />
                        {customerLinks.filter(l => l.link_type === 'phone').length} رقم
                      </Badge>
                      <Badge variant="secondary" className="gap-1">
                        <Users className="w-3 h-3" />
                        {customerLinks.filter(l => l.link_type === 'group').length} مجموعة
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid gap-3">
                    {customerLinks.map(link => (
                      <div 
                        key={link.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                          link.link_type === 'group' 
                            ? 'bg-purple-50 border-purple-200' 
                            : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            link.link_type === 'group' 
                              ? 'bg-purple-100' 
                              : 'bg-green-100'
                          }`}>
                            {link.link_type === 'group' ? (
                              <Users className="w-6 h-6 text-purple-600" />
                            ) : (
                              <Phone className="w-6 h-6 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-stone-800 text-lg">{link.customer_name}</p>
                            {link.shop_name && (
                              <p className="text-sm text-stone-500">🏪 {link.shop_name}</p>
                            )}
                            <p className="text-xs text-stone-400 font-mono mt-1" dir="ltr">
                              {link.phone || link.group_id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={link.link_type === 'group' ? 'secondary' : 'outline'} className="text-sm">
                            {link.link_type === 'group' ? '👥 مجموعة' : '📱 رقم'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (confirm('هل تريد حذف هذا الربط؟')) {
                                deleteCustomerLink(link.id);
                              }
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    قوالب الردود التلقائية
                  </CardTitle>
                  <CardDescription>أضف رسائل رد تلقائية بناءً على كلمات معينة</CardDescription>
                </div>
                <Button onClick={() => openTemplateDialog()} className="gap-2">
                  <Plus className="w-4 h-4" /> إضافة قالب
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-stone-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد قوالب. أضف قالباً للرد التلقائي.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg bg-white">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-stone-800">{template.name}</h4>
                            {template.is_active ? (
                              <Badge className="bg-green-100 text-green-700">مفعّل</Badge>
                            ) : (
                              <Badge variant="outline" className="text-stone-400">معطّل</Badge>
                            )}
                          </div>
                          {template.trigger && (
                            <p className="text-sm text-stone-500 mb-2">
                              <strong>الكلمات المفتاحية:</strong> {template.trigger}
                            </p>
                          )}
                          <p className="text-sm text-stone-600 bg-stone-50 p-2 rounded whitespace-pre-wrap">
                            {template.message}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openTemplateDialog(template)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteTemplate(template.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-stone-600" />
                إعدادات WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggles */}
              <div className="space-y-4">
                {/* Sound notification settings */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Volume2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <Label className="font-medium">إشعار صوتي للرسائل الجديدة</Label>
                      <p className="text-sm text-stone-500">تشغيل صوت عند وصول رسالة جديدة</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={playNotificationSound}
                      className="gap-2"
                    >
                      <Bell className="w-4 h-4" />
                      اختبار الصوت
                    </Button>
                    <Switch 
                      checked={audioEnabled}
                      onCheckedChange={setAudioEnabled}
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <Label className="font-medium">معالجة رسائل المجموعات</Label>
                    <p className="text-sm text-stone-500">تحليل واكتشاف الطلبات من المجموعات</p>
                  </div>
                  <Switch 
                    checked={settings.process_groups}
                    onCheckedChange={(checked) => setSettings({ ...settings, process_groups: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <Label className="font-medium">الرد التلقائي على التحيات</Label>
                    <p className="text-sm text-stone-500">رد تلقائي عند إرسال مرحبا أو السلام</p>
                  </div>
                  <Switch 
                    checked={settings.auto_reply_greeting}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_reply_greeting: checked })}
                  />
                </div>
              </div>

              {/* Message Templates */}
              <div className="space-y-4">
                <h4 className="font-medium text-stone-700">رسائل مخصصة</h4>
                
                <div className="space-y-2">
                  <Label>رسالة الترحيب</Label>
                  <Textarea 
                    value={settings.greeting_message || ''}
                    onChange={(e) => setSettings({ ...settings, greeting_message: e.target.value })}
                    placeholder="مرحباً بك! كيف يمكنني مساعدتك؟"
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>رسالة تأكيد الطلب</Label>
                  <Textarea 
                    value={settings.order_confirmation_message || ''}
                    onChange={(e) => setSettings({ ...settings, order_confirmation_message: e.target.value })}
                    placeholder="استخدم: {order_number}, {customer_name}, {items}, {total}"
                    rows={4}
                  />
                  <p className="text-xs text-stone-400">المتغيرات: {'{order_number}'}, {'{customer_name}'}, {'{items}'}, {'{total}'}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>رسالة طلب التوضيح</Label>
                  <Textarea 
                    value={settings.clarification_message || ''}
                    onChange={(e) => setSettings({ ...settings, clarification_message: e.target.value })}
                    placeholder="عذراً، لم أفهم طلبك..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>رسالة عميل غير مسجل</Label>
                  <Textarea 
                    value={settings.customer_not_found_message || ''}
                    onChange={(e) => setSettings({ ...settings, customer_not_found_message: e.target.value })}
                    placeholder="عذراً، رقمك غير مسجل..."
                    rows={3}
                  />
                </div>
              </div>

              <Button onClick={saveSettings} className="gap-2" disabled={savingSettings}>
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                حفظ الإعدادات
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'تعديل القالب' : 'إضافة قالب جديد'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>اسم القالب</Label>
              <Input 
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="مثال: تحية، ساعات العمل"
              />
            </div>
            <div className="space-y-2">
              <Label>الكلمات المفتاحية (مفصولة بفاصلة)</Label>
              <Input 
                value={templateForm.trigger}
                onChange={(e) => setTemplateForm({ ...templateForm, trigger: e.target.value })}
                placeholder="مثال: مرحبا, السلام, صباح"
              />
              <p className="text-xs text-stone-400">عند وجود أي كلمة من هذه الكلمات سيتم الرد تلقائياً</p>
            </div>
            <div className="space-y-2">
              <Label>نص الرد</Label>
              <Textarea 
                value={templateForm.message}
                onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                placeholder="نص الرد التلقائي..."
                rows={4}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={templateForm.is_active}
                onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_active: checked })}
              />
              <Label>مفعّل</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>إلغاء</Button>
            <Button onClick={saveTemplate}>حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>إرسال رد</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="text-sm text-stone-500">الرد على:</p>
              <p className="font-medium">+{replyTo?.from_phone || replyTo?.from}</p>
            </div>
            
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label>اختر قالب سريع:</Label>
                <div className="flex flex-wrap gap-2">
                  {templates.filter(t => t.is_active).map(t => (
                    <Button 
                      key={t.id} 
                      variant="outline" 
                      size="sm"
                      onClick={() => setReplyMessage(t.message)}
                    >
                      {t.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>الرسالة</Label>
              <Textarea 
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="اكتب رسالتك..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>إلغاء</Button>
            <Button onClick={sendReply} disabled={sendingReply || !replyMessage.trim()} className="gap-2">
              {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              إرسال
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-600" />
              إنشاء طلب جديد
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-y-auto px-1">
            {/* Customer Info */}
            <div className="p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-blue-500" />
                {selectedCustomer ? (
                  <span className="font-bold text-green-700">{selectedCustomer.name}</span>
                ) : (
                  <span className="text-orange-600">عميل غير مسجل</span>
                )}
                <span className="text-stone-400 text-sm" dir="ltr">+{selectedMessage?.from_phone}</span>
              </div>
              {selectedCustomer?.shop_name && (
                <p className="text-sm text-stone-500">🏪 {selectedCustomer.shop_name}</p>
              )}
              {!selectedCustomer && (
                <div className="mt-2">
                  <Label className="text-sm">اختر العميل:</Label>
                  <select 
                    className="w-full mt-1 p-2 border rounded-lg"
                    value={selectedCustomer?.id || ''}
                    onChange={(e) => setSelectedCustomer(customers.find(c => c.id === e.target.value))}
                  >
                    <option value="">-- اختر العميل --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.name} {c.shop_name ? `- ${c.shop_name}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Products Grid */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                المنتجات المتاحة
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[30vh] overflow-y-auto p-2 border rounded-lg bg-white touch-pan-y">
                {products.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addItemToOrder(product)}
                    className="p-2 text-right border rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors active:bg-green-100"
                  >
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-stone-500">{(product.default_price || 0).toFixed(2)}€</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Order Items */}
            {orderItems.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  الطلب ({orderItems.length} منتج)
                </Label>
                <div className="space-y-2 p-3 border rounded-lg bg-green-50">
                  {orderItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between gap-2 p-2 bg-white rounded border">
                      <span className="font-medium flex-1">{item.product_name}</span>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updateItemQuantity(index, item.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <button 
                          onClick={() => updateItemQuantity(index, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
                        >
                          +
                        </button>
                        <span className="text-sm text-stone-500 w-16 text-left">
                          {(item.price * item.quantity).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center pt-2 mt-2 border-t border-green-200">
                    <span className="font-bold">الإجمالي:</span>
                    <span className="font-bold text-lg text-green-700">
                      {orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}€
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>إلغاء</Button>
            <Button 
              onClick={createOrderFromWhatsApp} 
              disabled={creatingOrder || orderItems.length === 0 || !selectedCustomer}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {creatingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              إنشاء الطلب وإرسال تأكيد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Statement Dialog */}
      <Dialog open={statementDialogOpen} onOpenChange={setStatementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              إرسال كشف حساب
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="p-4 bg-stone-50 rounded-lg">
              {selectedCustomer ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-blue-500" />
                    <span className="font-bold text-lg">{selectedCustomer.name}</span>
                  </div>
                  {selectedCustomer.shop_name && (
                    <p className="text-stone-600 mb-3">🏪 {selectedCustomer.shop_name}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                    <div>
                      <p className="text-sm text-stone-500">إجمالي المبيعات</p>
                      <p className="font-bold text-lg">{(selectedCustomer.total_debt || 0).toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">إجمالي المدفوع</p>
                      <p className="font-bold text-lg text-green-600">{(selectedCustomer.total_paid || 0).toFixed(2)}€</p>
                    </div>
                  </div>
                  
                  <div className={`mt-3 p-3 rounded-lg text-center ${
                    (selectedCustomer.balance || 0) > 0 ? 'bg-red-100 text-red-700' : 
                    (selectedCustomer.balance || 0) < 0 ? 'bg-green-100 text-green-700' : 
                    'bg-gray-100 text-gray-700'
                  }`}>
                    <p className="text-sm">الرصيد</p>
                    <p className="font-bold text-2xl">
                      {(selectedCustomer.balance || 0) > 0 
                        ? `${(selectedCustomer.balance || 0).toFixed(2)}€ مستحق`
                        : (selectedCustomer.balance || 0) < 0 
                        ? `${Math.abs(selectedCustomer.balance || 0).toFixed(2)}€ له`
                        : 'مُسدد'}
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-orange-600">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                  <p>العميل غير مسجل في النظام</p>
                  <p className="text-sm text-stone-500 mt-1">لا يمكن إرسال كشف حساب</p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatementDialogOpen(false)}>إلغاء</Button>
            <Button 
              onClick={sendCustomerStatement} 
              disabled={sendingStatement || !selectedCustomer}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {sendingStatement ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              إرسال كشف الحساب
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Customer Dialog - for unrecognized phone numbers */}
      <Dialog open={linkCustomerDialogOpen} onOpenChange={setLinkCustomerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              ربط العميل وإنشاء الطلب
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Message preview */}
            {pendingMessage && (
              <div className="p-3 bg-stone-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  {pendingMessage.is_group ? (
                    <Users className="w-4 h-4 text-purple-500" />
                  ) : (
                    <Phone className="w-4 h-4 text-stone-500" />
                  )}
                  <span className="text-sm text-stone-600 font-numbers">{pendingMessage.from_phone}</span>
                  {pendingMessage.is_group && (
                    <Badge variant="outline" className="text-purple-600 text-xs">مجموعة</Badge>
                  )}
                </div>
                <p className="text-sm bg-white p-2 rounded border">{pendingMessage.body}</p>
              </div>
            )}
            
            {/* Customer selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                اختر العميل
              </Label>
              <select
                value={selectedCustomer?.id || ''}
                onChange={(e) => {
                  const customer = customers.find(c => c.id === e.target.value);
                  setSelectedCustomer(customer || null);
                }}
                className="w-full p-2 border rounded-lg bg-white"
              >
                <option value="">-- اختر العميل --</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.shop_name ? `(${c.shop_name})` : ''} {c.phone ? `- ${c.phone}` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Selected customer info */}
            {selectedCustomer && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-bold text-green-800">{selectedCustomer.name}</p>
                    {selectedCustomer.shop_name && (
                      <p className="text-sm text-green-600">{selectedCustomer.shop_name}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Save link option */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveLink}
                  onChange={(e) => setSaveLink(e.target.checked)}
                  className="w-4 h-4 rounded border-amber-400 text-amber-600"
                />
                <div>
                  <span className="text-sm font-medium text-amber-800">
                    {pendingMessage?.is_group ? '📱 ربط المجموعة بالعميل دائماً' : '📱 ربط الرقم بالعميل دائماً'}
                  </span>
                  <p className="text-xs text-amber-600">
                    الرسائل القادمة من {pendingMessage?.is_group ? 'هذه المجموعة' : 'هذا الرقم'} ستُعرَف تلقائياً
                  </p>
                </div>
              </label>
            </div>
            
            {/* Info message */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
              <Sparkles className="w-4 h-4 inline ml-1" />
              سيتم تحليل الرسالة بالذكاء الاصطناعي وإنشاء الطلب تلقائياً
            </div>
          </div>
          
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setLinkCustomerDialogOpen(false)}>
              إلغاء
            </Button>
            <Button 
              variant="outline"
              onClick={createOrderAfterLinking}
              disabled={!selectedCustomer}
              className="gap-2"
            >
              <Package className="w-4 h-4" />
              إنشاء يدوي
            </Button>
            <Button 
              onClick={processMessageWithAI}
              disabled={processingAutoOrder || !selectedCustomer}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {processingAutoOrder ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              إنشاء تلقائي بـ AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Links Management Dialog */}
      <Dialog open={linksDialogOpen} onOpenChange={setLinksDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-600" />
              إدارة روابط الأرقام والمجموعات
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingLinks ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
              </div>
            ) : customerLinks.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>لا توجد روابط محفوظة</p>
                <p className="text-sm text-stone-400 mt-1">
                  عند ربط عميل برقم أو مجموعة، سيظهر هنا
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {customerLinks.map(link => (
                  <div 
                    key={link.id} 
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      {link.link_type === 'group' ? (
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-stone-800">{link.customer_name}</p>
                        {link.shop_name && (
                          <p className="text-sm text-stone-500">{link.shop_name}</p>
                        )}
                        <p className="text-xs text-stone-400 font-numbers" dir="ltr">
                          {link.phone || link.group_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={link.link_type === 'group' ? 'secondary' : 'outline'}>
                        {link.link_type === 'group' ? 'مجموعة' : 'رقم'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteCustomerLink(link.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinksDialogOpen(false)}>
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppConnectPage;
